/**
 * ODP XML manipulation via fast-xml-parser.
 *
 * Why fast-xml-parser instead of DOMParser+XMLSerializer:
 *   Browser XMLSerializer mutates namespace prefixes on round-trip,
 *   which breaks ODP files that use 15+ namespace prefixes.
 *   fast-xml-parser with preserveOrder preserves the original structure.
 */

import { XMLParser, XMLBuilder } from "fast-xml-parser";

const PARSER_OPTS = {
  ignoreAttributes: false,
  preserveOrder: true,
  attributeNamePrefix: "@_",
  allowBooleanAttributes: true,
  parseTagValue: false,
  trimValues: false,
  processEntities: false,
  ignorePiTags: false,
  commentPropName: "#comment",
};

const BUILDER_OPTS = {
  ignoreAttributes: false,
  preserveOrder: true,
  attributeNamePrefix: "@_",
  suppressBooleanAttributes: false,
  format: false,
  processEntities: false,
  commentPropName: "#comment",
};

export function parseXml(xml: string): any[] {
  const parser = new XMLParser(PARSER_OPTS);
  return parser.parse(xml);
}

export function buildXml(parsed: any[]): string {
  const builder = new XMLBuilder(BUILDER_OPTS);
  const result = builder.build(parsed);
  if (!result.startsWith("<?xml")) {
    return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n' + result;
  }
  return result;
}

/**
 * Find an element by tag name in a preserveOrder tree.
 * preserveOrder format: [{ "tag:name": [...children], ":@": { attrs } }, ...]
 */
export function findElements(nodes: any[], tagName: string): any[] {
  const results: any[] = [];
  for (const node of nodes) {
    if (tagName in node) {
      results.push(node);
    }
    for (const key of Object.keys(node)) {
      if (key === ":@" || key.startsWith("#")) continue;
      const children = node[key];
      if (Array.isArray(children)) {
        results.push(...findElements(children, tagName));
      }
    }
  }
  return results;
}

export function getAttr(node: any, attr: string): string | undefined {
  return node[":@"]?.[`@_${attr}`];
}

export function setAttr(node: any, attr: string, value: string): void {
  if (!node[":@"]) node[":@"] = {};
  node[":@"][`@_${attr}`] = value;
}

export function getChildren(node: any): any[] {
  for (const key of Object.keys(node)) {
    if (key === ":@" || key.startsWith("#")) continue;
    if (Array.isArray(node[key])) return node[key];
  }
  return [];
}

export function getTagName(node: any): string {
  for (const key of Object.keys(node)) {
    if (key !== ":@" && !key.startsWith("#")) return key;
  }
  return "";
}

export function makeElement(tag: string, attrs: Record<string, string>, children: any[] = []): any {
  const el: any = { [tag]: children };
  if (Object.keys(attrs).length > 0) {
    el[":@"] = {};
    for (const [k, v] of Object.entries(attrs)) {
      el[":@"][`@_${k}`] = v;
    }
  }
  return el;
}

export function makeTextElement(tag: string, attrs: Record<string, string>, text: string): any {
  const el: any = { [tag]: [{ "#text": text }] };
  if (Object.keys(attrs).length > 0) {
    el[":@"] = {};
    for (const [k, v] of Object.entries(attrs)) {
      el[":@"][`@_${k}`] = v;
    }
  }
  return el;
}
